import axios from 'axios'
import dayjs from 'dayjs'

import { useMarketingFlowTemplates } from '@/composables/marketingFlowsTemplates'
import useStore from '@/composables/store'
import { isClickTriggeredAction } from '@/views/autopilot/clickTrigger'
import { getMarketingFlowPreviewText } from '@/views/autopilot/marketingFlowMessage'
import type {
  ActionSettings,
  DelaySettings,
  ExternalApiAuthType,
  MarketingAction,
  MarketingFlowCreatePayload,
  TiggerFilter,
  TriggerSettings
} from '@/views/autopilot/types/marketingFlow'
import {
  getDelayMinutesDivValueFromUnit,
  getDelayMinutesValueFromUnit,
  isNumber,
  isTransactionalDefaultTrigger
} from '@/views/autopilot/utils'

import type { Audience, LoyaltyReward } from '@/types/types'
import type { ContentScope } from '@/types/contentScope'

export default function useMarketingFlow() {
  const route = useRoute()
  const { isUsingTemplate } = useMarketingFlowTemplates()
  const { storesWithoutReviewLink } = useStore()

  const templateUsed = computed(() => route.query.fromTemplate)

  const externalApiAuthTypesRequiringConfig: ExternalApiAuthType[] = ['BEARER', 'BASIC']

  const getExternalApiHeadersMap = (action: MarketingAction): Record<string, string> | null => {
    if (!action.externalApiHeaders?.length) {
      return null
    }

    const headers = action.externalApiHeaders.reduce<Record<string, string>>((acc, header) => {
      const key = header.key?.trim()
      if (!key) {
        return acc
      }

      acc[key] = header.value ?? ''
      return acc
    }, {})

    return Object.keys(headers).length > 0 ? headers : null
  }

  const hasExternalApiAuthConfigValues = (action: MarketingAction) => {
    if (!action.externalApiAuthConfig || typeof action.externalApiAuthConfig !== 'object') {
      return false
    }

    const hasNonEmptyValue = (value: unknown) => typeof value === 'string' && value.trim().length > 0

    if (action.externalApiAuthType === 'BEARER') {
      return Boolean('token' in action.externalApiAuthConfig && hasNonEmptyValue(action.externalApiAuthConfig.token))
    }

    if (action.externalApiAuthType === 'BASIC') {
      return Boolean(
        'username' in action.externalApiAuthConfig &&
          'password' in action.externalApiAuthConfig &&
          hasNonEmptyValue(action.externalApiAuthConfig.username) &&
          hasNonEmptyValue(action.externalApiAuthConfig.password)
      )
    }

    return false
  }

  const getExternalApiAuthenticationConfig = (action: MarketingAction): Record<string, unknown> | undefined => {
    if (!action.externalApiAuthType || action.externalApiAuthType === 'NONE') {
      return undefined
    }

    if (!action.externalApiAuthConfig || typeof action.externalApiAuthConfig !== 'object') {
      return undefined
    }

    return action.externalApiAuthConfig as Record<string, unknown>
  }

  const buildExternalApiPayload = (action: MarketingAction) => {
    const authType = action.externalApiAuthType ?? 'NONE'
    const headers = getExternalApiHeadersMap(action)
    const shouldSendAuthConfig =
      authType !== 'NONE' && (action.isExternalApiAuthConfigDirty || hasExternalApiAuthConfigValues(action))
    const config = getExternalApiAuthenticationConfig(action)

    return {
      url: action.externalApiUrl?.trim() ?? '',
      method: action.externalApiMethod ?? 'POST',
      ...(headers && { headers }),
      ...(action.externalApiBodyTemplate && { body_template: action.externalApiBodyTemplate }),
      authentication: {
        type: authType,
        ...(shouldSendAuthConfig && config && { config })
      }
    }
  }

  const buildActionPayload = (action: MarketingAction, isTransactional?: boolean) => ({
    type: action.actionType,
    is_active: true,
    message_content: action.messageContent,
    bonus_multiplier: action.bonusMultiplier,
    bonus_points: isNumber(action.bonusPoints) ? Number(action.bonusPoints) : null,
    reward_id: action.selectedReward,
    is_other_channel_active: action.preferPushChannel || action.preferSmsChannel,
    ...((action.actionType === 'SEND_SMS' || action.actionType === 'SEND_PUSH') && {
      is_transactional: isTransactional ?? null
    }),
    ...(action.actionType === 'SEND_EMAIL' && {
      email_content_id: action.selectedEmailTemplate ? Number(action.selectedEmailTemplate) : null
    }),
    ...(action.actionType === 'CALL_EXTERNAL_API' && {
      external_api: buildExternalApiPayload(action)
    }),
    is_triggered_on_click: isClickTriggeredAction(action.actionType) && Boolean(action.isTriggeredOnClick)
  })

  const isValidJsonBodyTemplate = (template: string): boolean => {
    try {
      JSON.parse(template.replace(/\{\{[^}]+\}\}/g, 'null'))
      return true
    } catch {
      return false
    }
  }

  const getLegacyExternalApiAuthConfig = (authConfig: unknown): Record<string, unknown> | null => {
    if (!authConfig) {
      return null
    }

    if (typeof authConfig === 'string') {
      try {
        const parsed = JSON.parse(authConfig)
        return parsed && typeof parsed === 'object' ? parsed : null
      } catch {
        return null
      }
    }

    if (typeof authConfig === 'object') {
      return authConfig as Record<string, unknown>
    }

    return null
  }

  const getMarketingFlowErrors = (
    triggerSettings: TriggerSettings,
    delaySettings: DelaySettings,
    actionSettings: ActionSettings
  ): Array<{
    type: 'trigger' | 'delay' | 'action'
    field: string
    i18nKey: string
    severity: 'invalid' | 'incomplete'
  }> => {
    return [
      ...getTriggerStepErrors(triggerSettings),
      ...getDelayStepErrors(delaySettings, triggerSettings.triggerType),
      ...getActionStepErrors(actionSettings, triggerSettings.selectedStores)
    ]
  }

  const getTriggerStepErrors = (
    triggerSettings: TriggerSettings
  ): Array<{ type: 'trigger'; field: string; i18nKey: string; severity: 'invalid' | 'incomplete' }> => {
    const errors: Array<{ type: 'trigger'; field: string; i18nKey: string; severity: 'invalid' | 'incomplete' }> = []
    if (!triggerSettings.selectedStores?.length) {
      errors.push({ type: 'trigger', field: 'selectedStores', i18nKey: 'requiredField', severity: 'incomplete' })
    }

    if (triggerSettings.triggerType === 'SEGMENT_ENTRY' || triggerSettings.triggerType === 'SEGMENT_EXIT') {
      if (!triggerSettings.selectedAudience) {
        errors.push({ type: 'trigger', field: 'selectedAudience', i18nKey: 'requiredField', severity: 'incomplete' })
      }
    }

    if (
      triggerSettings.triggerType === 'CHECKIN' &&
      triggerSettings.checkinCount === 'custom' &&
      !triggerSettings.customCheckinCount
    ) {
      errors.push({
        type: 'trigger',
        field: 'customCheckinCount',
        i18nKey: 'requiredField',
        severity: 'incomplete'
      })
    }
    if (
      triggerSettings.triggerType === 'CHECKIN' &&
      triggerSettings.checkinCount === 'custom' &&
      triggerSettings.customCheckinCount &&
      !isNumber(triggerSettings.customCheckinCount)
    ) {
      errors.push({
        type: 'trigger',
        field: 'customCheckinCount',
        i18nKey: 'invalidNumber',
        severity: 'invalid'
      })
    }

    if (triggerSettings.triggerType === 'REWARD_RETRIEVAL' || triggerSettings.triggerType === 'REWARD_NEAR_TO_UNLOCK') {
      if (!triggerSettings.selectedRewards?.filter((reward) => reward !== null)?.length) {
        errors.push({ type: 'trigger', field: 'selectedRewards', i18nKey: 'requiredField', severity: 'incomplete' })
      }
    }

    if (triggerSettings.triggerType === 'REWARD_NEAR_TO_UNLOCK') {
      if (
        triggerSettings.pointTreshold === null ||
        triggerSettings.pointTreshold === undefined ||
        triggerSettings.pointTreshold === ''
      ) {
        errors.push({ type: 'trigger', field: 'pointTreshold', i18nKey: 'requiredField', severity: 'incomplete' })
      } else if (!isNumber(triggerSettings.pointTreshold)) {
        errors.push({ type: 'trigger', field: 'pointTreshold', i18nKey: 'invalidNumber', severity: 'invalid' })
      } else if (Number(triggerSettings.pointTreshold) < 1) {
        errors.push({ type: 'trigger', field: 'pointTreshold', i18nKey: 'pointTresholdMinValue', severity: 'invalid' })
      }
    }

    const hasMultipleTimeScenario = triggerSettings.multipleTimeScenario

    if (hasMultipleTimeScenario) {
      if (triggerSettings.maxTriggersPerUser && !isNumber(triggerSettings.maxTriggersPerUser)) {
        errors.push({ type: 'trigger', field: 'maxTriggersPerUser', i18nKey: 'invalidNumber', severity: 'invalid' })
      }
      if (triggerSettings.maxTriggersPerUser && Number(triggerSettings.maxTriggersPerUser) <= 1) {
        errors.push({
          type: 'trigger',
          field: 'maxTriggersPerUser',
          i18nKey: 'maxTriggersPerUserTooLow',
          severity: 'invalid'
        })
      }

      if (triggerSettings.cooldownMinutes && !isNumber(triggerSettings.cooldownMinutes)) {
        errors.push({ type: 'trigger', field: 'cooldownMinutes', i18nKey: 'invalidNumber', severity: 'invalid' })
      }
    }

    const startTime = triggerSettings.filters?.find((filter) => filter.type === 'START_DATETIME')?.value
    const endTime = triggerSettings.filters?.find((filter) => filter.type === 'END_DATETIME')?.value
    const timeRange = triggerSettings.filters?.find((filter) => filter.type === 'TIME_RANGE')?.value

    if (triggerSettings.triggerType === 'CHECKIN' && startTime && endTime) {
      const startDate = dayjs(startTime)
      const endDate = dayjs(endTime)

      if ((startDate.isAfter(endDate) || startDate.isSame(endDate, 'day')) && triggerSettings.hasEndDate) {
        errors.push({
          type: 'trigger',
          field: 'filters',
          i18nKey: 'startDateAfterEndDate',
          severity: 'invalid'
        })
      }

      if ((startDate.isAfter(endDate) || startDate.isSame(endDate)) && !triggerSettings.hasEndDate) {
        errors.push({
          type: 'trigger',
          field: 'filters',
          i18nKey: 'startTimeAfterEndTime',
          severity: 'invalid'
        })
      }
    }

    if (timeRange) {
      const [startStringTime, endStringTime] = timeRange.split(' - ')

      const referenceDate = dayjs().format('YYYY-MM-DD')
      const startTimeParsed = dayjs(`${referenceDate}T${startStringTime}`)
      const endTimeParsed = dayjs(`${referenceDate}T${endStringTime}`)

      // Check if start time is after or equal to end time (invalid)
      if (startTimeParsed.isAfter(endTimeParsed) || startTimeParsed.isSame(endTimeParsed)) {
        errors.push({ type: 'trigger', field: 'filters', i18nKey: 'startTimeAfterEndTime', severity: 'invalid' })
      }
    }

    return errors
  }

  const isTriggerStepCompleted = (triggerSettings: TriggerSettings) => {
    const errors = getTriggerStepErrors(triggerSettings)

    return errors.length === 0
  }

  const getDelayStepErrors = (
    delaySettings: DelaySettings,
    triggerType?: string | null
  ): Array<{ type: 'delay'; field: string; i18nKey: string; severity: 'invalid' | 'incomplete' }> => {
    const errors: Array<{ type: 'delay'; field: string; i18nKey: string; severity: 'invalid' | 'incomplete' }> = []

    // Pour le trigger BIRTHDAY, validation simplifiée
    if (triggerType === 'BIRTHDAY') {
      if (!delaySettings.sendAtSpecificHour) {
        errors.push({ type: 'delay', field: 'sendAtSpecificHour', i18nKey: 'requiredField', severity: 'incomplete' })
      }
      if (!delaySettings.hourToSend) {
        errors.push({ type: 'delay', field: 'hourToSend', i18nKey: 'requiredField', severity: 'incomplete' })
      }
      return errors
    }

    // Pour les autres triggers, validation normale
    if (!delaySettings.sendImmediatlyAfterTrigger) {
      if (!delaySettings.sendAfterDelay) {
        errors.push({ type: 'delay', field: 'sendAfterDelay', i18nKey: 'requiredField', severity: 'incomplete' })
      } else {
        if (delaySettings.daysBeforeAction === null || delaySettings.daysBeforeAction === '') {
          errors.push({ type: 'delay', field: 'daysBeforeAction', i18nKey: 'requiredField', severity: 'incomplete' })
        }

        if (delaySettings.daysBeforeAction && !isNumber(delaySettings.daysBeforeAction)) {
          errors.push({ type: 'delay', field: 'daysBeforeAction', i18nKey: 'invalidNumber', severity: 'invalid' })
        }
        if (delaySettings.sendAtSpecificHour && !delaySettings.hourToSend) {
          errors.push({ type: 'delay', field: 'hourToSend', i18nKey: 'requiredField', severity: 'incomplete' })
        }
      }
    }

    return errors
  }

  const isDelayStepCompleted = (delaySettings: DelaySettings, triggerType?: string | null) => {
    const errors = getDelayStepErrors(delaySettings, triggerType)
    return errors.length === 0
  }
  const getActionStepErrors = (
    actionSettings: ActionSettings,
    storeIds: number[]
  ): Array<{ type: 'action'; field: string; i18nKey: string; severity: 'invalid' | 'incomplete' }> => {
    const errors: Array<{ type: 'action'; field: string; i18nKey: string; severity: 'invalid' | 'incomplete' }> = []

    if (actionSettings.actions.length === 0) {
      errors.push({ type: 'action', field: 'actions', i18nKey: 'requiredField', severity: 'incomplete' })
      return errors
    }

    const messageAction = actionSettings.actions.find(
      (action) => action.actionType === 'SEND_SMS' || action.actionType === 'SEND_PUSH'
    )
    const emailAction = actionSettings.actions.find((action) => action.actionType === 'SEND_EMAIL')
    const rewardAction = actionSettings.actions.find((action) => action.actionType === 'REWARD')
    const bonusAction = actionSettings.actions.find((action) => action.actionType === 'BONUS_POINTS')
    const bonusMultiplierAction = actionSettings.actions.find((action) => action.actionType === 'BONUS_MULTIPLIER')
    const externalApiAction = actionSettings.actions.find((action) => action.actionType === 'CALL_EXTERNAL_API')

    const hasRewardLinkInMessage = /\{\{\s*rewardLink\s*\}\}/.test(messageAction?.messageContent || '')
    const hasRewardLinkWithoutReward = hasRewardLinkInMessage && !rewardAction?.selectedReward

    const hasReviewLinkInMessage = /\{\{\s*reviewLink\s*\}\}/.test(messageAction?.messageContent || '')
    const hasStoreWithoutReviewLink = storeIds.some(
      (storeId) => storesWithoutReviewLink.value.includes(storeId) && hasReviewLinkInMessage
    )

    if (
      (messageAction || rewardAction) &&
      actionSettings.conversionDelay &&
      !isNumber(actionSettings.conversionDelay)
    ) {
      errors.push({ type: 'action', field: 'conversionDelay', i18nKey: 'invalidNumber', severity: 'invalid' })
    }

    if (messageAction && messageAction.messageContent === '') {
      errors.push({ type: 'action', field: 'messageContent', i18nKey: 'requiredField', severity: 'incomplete' })
    }

    const maxMessageLength = actionSettings.isTransactional ? 160 : 145
    if (messageAction && (actionSettings.computedMessageLength ?? 0) > maxMessageLength) {
      errors.push({ type: 'action', field: 'messageContent', i18nKey: 'messageTooLong', severity: 'invalid' })
    }

    if (messageAction && actionSettings.messageError) {
      errors.push({
        type: 'action',
        field: 'messageContent',
        i18nKey: actionSettings.messageError,
        severity: 'invalid'
      })
    }

    if (emailAction && !emailAction.selectedEmailTemplate) {
      errors.push({ type: 'action', field: 'selectedEmailTemplate', i18nKey: 'requiredField', severity: 'incomplete' })
    }

    if (rewardAction && !rewardAction.selectedReward) {
      errors.push({ type: 'action', field: 'selectedReward', i18nKey: 'requiredField', severity: 'incomplete' })
    }

    if (bonusAction && !bonusAction.bonusPoints) {
      errors.push({ type: 'action', field: 'bonusPoints', i18nKey: 'requiredField', severity: 'incomplete' })
    }
    if (bonusAction && bonusAction.bonusPoints && !isNumber(bonusAction.bonusPoints)) {
      errors.push({ type: 'action', field: 'bonusPoints', i18nKey: 'invalidNumber', severity: 'invalid' })
    }

    if (bonusMultiplierAction && !bonusMultiplierAction.bonusMultiplier) {
      errors.push({ type: 'action', field: 'bonusMultiplier', i18nKey: 'requiredField', severity: 'incomplete' })
    }
    if (
      bonusMultiplierAction &&
      bonusMultiplierAction.bonusMultiplier &&
      !isNumber(bonusMultiplierAction.bonusMultiplier)
    ) {
      errors.push({ type: 'action', field: 'bonusMultiplier', i18nKey: 'invalidNumber', severity: 'invalid' })
    }

    if (hasRewardLinkWithoutReward) {
      errors.push({ type: 'action', field: 'rewardAction', i18nKey: 'requiredField', severity: 'invalid' })
    }

    if (hasStoreWithoutReviewLink) {
      errors.push({ type: 'action', field: 'messageContent', i18nKey: 'reviewLinkRequired', severity: 'invalid' })
    }

    if (externalApiAction) {
      const externalApiUrl = externalApiAction.externalApiUrl?.trim()
      if (!externalApiUrl) {
        errors.push({ type: 'action', field: 'externalApiUrl', i18nKey: 'requiredField', severity: 'incomplete' })
      } else if (!externalApiUrl.startsWith('https://')) {
        errors.push({
          type: 'action',
          field: 'externalApiUrl',
          i18nKey: 'externalApiUrlHttpsOnly',
          severity: 'invalid'
        })
      }

      if (!externalApiAction.externalApiMethod) {
        errors.push({ type: 'action', field: 'externalApiMethod', i18nKey: 'requiredField', severity: 'incomplete' })
      }

      const invalidHeader = externalApiAction.externalApiHeaders?.find(
        (header) => (header.key && !header.value) || (!header.key && header.value)
      )
      if (invalidHeader) {
        errors.push({
          type: 'action',
          field: 'externalApiHeaders',
          i18nKey: 'externalApiHeadersInvalid',
          severity: 'invalid'
        })
      }

      if (
        externalApiAction.externalApiBodyTemplateHasError ||
        (externalApiAction.externalApiBodyTemplate &&
          !isValidJsonBodyTemplate(externalApiAction.externalApiBodyTemplate))
      ) {
        errors.push({
          type: 'action',
          field: 'externalApiBodyTemplate',
          i18nKey: 'externalApiBodyTemplateInvalid',
          severity: 'invalid'
        })
      }

      if (!externalApiAction.externalApiAuthType) {
        errors.push({
          type: 'action',
          field: 'externalApiAuthType',
          i18nKey: 'requiredField',
          severity: 'incomplete'
        })
      } else if (externalApiAuthTypesRequiringConfig.includes(externalApiAction.externalApiAuthType)) {
        const hasAuthConfigValues = hasExternalApiAuthConfigValues(externalApiAction)
        if (!hasAuthConfigValues && externalApiAction.isExternalApiAuthConfigDirty) {
          errors.push({
            type: 'action',
            field: 'externalApiAuthConfig',
            i18nKey: 'requiredField',
            severity: 'incomplete'
          })
        }
      }
    }

    return errors
  }

  const isActionStepCompleted = (actionSettings: ActionSettings, storeIds: number[]) => {
    const errors = getActionStepErrors(actionSettings, storeIds)
    return errors.length === 0
  }

  const generateNewVersionName = (name: string) => {
    const regex = /^V([0-9]*) - /g
    const versionPatternMatch = name.match(regex)
    const versionNumber = versionPatternMatch ? Number(versionPatternMatch[0].match(/\d+/)[0]) : 1

    let newName = `V${versionNumber + 1} - ${name}`
    if (versionPatternMatch?.[0]) {
      newName = `V${versionNumber + 1} - ${name.slice(versionPatternMatch[0].length)}`
    }

    return newName
  }

  const formatPayloadCheckinCountValue = (triggerSettings: TriggerSettings) => {
    if (triggerSettings.checkinCount === 'custom') {
      return Number(triggerSettings.customCheckinCount)
    }

    return Number(triggerSettings.checkinCount)
  }

  const generateUpsertPayload = ({
    marketingFlowName,
    merchantId,
    isActive,
    triggerSettings,
    delaySettings,
    actionSettings,
    executeOnExistingMembers
  }: {
    marketingFlowName: string
    merchantId: number
    isActive: boolean
    triggerSettings: TriggerSettings
    delaySettings: DelaySettings
    actionSettings: ActionSettings
    executeOnExistingMembers?: boolean
  }): MarketingFlowCreatePayload => {
    const includedSegmentFilters: TiggerFilter[] =
      triggerSettings.includedSegments?.map((segment) => ({
        type: 'INCLUDED_SEGMENT',
        value: segment?.id?.toString()
      })) ?? []

    const excludedSegmentFilters: TiggerFilter[] =
      triggerSettings.excludedSegments?.map((segment) => ({
        type: 'EXCLUDED_SEGMENT',
        value: segment?.id?.toString()
      })) ?? []

    return {
      name: marketingFlowName,
      merchant_id: merchantId,

      is_active: isActive,
      is_draft: !isActive,

      conversion_delay_in_minutes: Number(actionSettings.conversionDelay) * 24 * 60,

      action_delay_in_minutes: delaySettings.sendAfterDelay
        ? getDelayMinutesValueFromUnit(Number(delaySettings.daysBeforeAction), delaySettings.delayUnit)
        : null,
      action_delay_time: delaySettings.sendAtSpecificHour ? delaySettings.hourToSend : null,
      action_delay_unit: delaySettings.sendAfterDelay ? delaySettings.delayUnit : 'DAYS',

      stores: triggerSettings.selectedStores ?? null,

      ...(executeOnExistingMembers !== undefined ? { execute_on_existing_members: executeOnExistingMembers } : {}),

      triggers: [
        {
          type: triggerSettings.triggerType,
          is_active: true,
          merchant_segment_id: triggerSettings.selectedAudience,
          reward_ids: triggerSettings.selectedRewards?.filter((rewardId) => rewardId !== null),
          cooldown_minutes: Number(triggerSettings.cooldownMinutes),
          max_triggers_per_user: triggerSettings.maxTriggersPerUser ? Number(triggerSettings.maxTriggersPerUser) : null,
          date_relative_offset_days: triggerSettings.dateRelativeOffsetDays
            ? Number(triggerSettings.dateRelativeOffsetDays)
            : 0,
          date_relative_offset_direction: 'BEFORE',
          filters: [
            ...includedSegmentFilters,
            ...excludedSegmentFilters,
            ...(triggerSettings.filters?.filter(
              (filter) => filter.type !== 'INCLUDED_SEGMENT' && filter.type !== 'EXCLUDED_SEGMENT'
            ) ?? [])
          ],
          point_treshold:
            triggerSettings.pointTreshold && isNumber(triggerSettings.pointTreshold)
              ? Number(triggerSettings.pointTreshold)
              : null,
          ...(formatPayloadCheckinCountValue(triggerSettings)
            ? { checkin_count: formatPayloadCheckinCountValue(triggerSettings) }
            : {})
        }
      ],

      actions: actionSettings.actions.map((action) => buildActionPayload(action, actionSettings.isTransactional))
    }
  }

  const getMarketingFlowLastUpdateDate = (marketingFlow) => {
    let updatedAt = new Date(marketingFlow.updated_at)

    marketingFlow.actions.map((action) => {
      const actionUpdatedAt = new Date(action.updated_at)
      if (actionUpdatedAt > updatedAt) {
        updatedAt = actionUpdatedAt
      }
    })

    marketingFlow.triggers.map((trigger) => {
      const triggerUpdatedAt = new Date(trigger.updated_at)
      if (triggerUpdatedAt > updatedAt) {
        updatedAt = triggerUpdatedAt
      }
    })

    return updatedAt
  }

  const formatCheckinCountValue = (checkinCount: number | null) => {
    if (checkinCount) {
      if (checkinCount > 5) {
        return {
          checkinCount: 'custom',
          customCheckinCount: checkinCount.toString()
        }
      }

      return {
        checkinCount: checkinCount.toString(),
        customCheckinCount: null
      }
    }

    return {
      checkinCount: null,
      customCheckinCount: null
    }
  }

  const fetchMarketingFlow = async ({
    marketingFlowId,
    audiences,
    rewards
  }: {
    marketingFlowId: number
    audiences: Audience[]
    rewards: LoyaltyReward[]
  }): Promise<{
    lastUpdatedAtValue: Date
    marketingFlowNameValue: string
    triggerSettingsValue: TriggerSettings
    delaySettingsValue: DelaySettings
    actionSettingsValue: ActionSettings
    isActiveValue: boolean
    activatedAtValue: string | null
    templateUsedValue: string | null
    scopeValue: ContentScope | null
  }> => {
    const { data } = await axios.get(`/marketing-flows/${marketingFlowId}`)
    const marketingFlow = data.data

    const marketingFlowTrigger = marketingFlow.triggers[0]

    const lastUpdatedAtValue = getMarketingFlowLastUpdateDate(marketingFlow)

    const marketingFlowNameValue = marketingFlow.name

    const isActiveValue = !marketingFlow.is_draft
    const activatedAtValue: string | null = marketingFlow.activated_at ?? null
    const templateUsedValue = marketingFlow.template_used ?? null
    const scopeValue: ContentScope | null = marketingFlow.scope ?? null

    const selectedStores = marketingFlow.stores.map((store: { id: number; name: string }) => store.id)

    const startDate = marketingFlowTrigger?.filters?.find((filter) => filter.type === 'START_DATETIME')?.value
    const endDate = marketingFlowTrigger?.filters?.find((filter) => filter.type === 'END_DATETIME')?.value
    const hasEndDate = endDate && startDate && !dayjs(startDate).isSame(dayjs(endDate), 'day')

    const includedSegmentIds: number[] = marketingFlowTrigger?.filters
      ?.filter((filter) => filter.type === 'INCLUDED_SEGMENT')
      ?.map((filter) => Number(filter.value))
    const excludedSegmentIds: number[] = marketingFlowTrigger?.filters
      ?.filter((filter) => filter.type === 'EXCLUDED_SEGMENT')
      ?.map((filter) => Number(filter.value))

    const triggerSettingsValue = {
      triggerType: marketingFlowTrigger?.type,
      selectedAudience: marketingFlowTrigger?.merchant_segment_id ?? null,
      selectedRewards: marketingFlowTrigger?.rewards?.map((reward) => reward.id),
      includedSegments: includedSegmentIds?.map((segmentId) => audiences.find((audience) => audience.id === segmentId)),
      excludedSegments: excludedSegmentIds?.map((segmentId) => audiences.find((audience) => audience.id === segmentId)),
      cooldownMinutes: marketingFlowTrigger?.cooldown_minutes,
      selectedStores: selectedStores,
      maxTriggersPerUser: marketingFlowTrigger?.max_triggers_per_user,
      filters: marketingFlowTrigger?.filters,
      dateRelativeOffsetDays: marketingFlowTrigger?.date_relative_offset_days ?? 0,
      dateRelativeOffsetDirection: marketingFlowTrigger?.date_relative_offset_direction ?? 'BEFORE',
      pointTreshold: marketingFlowTrigger?.point_treshold,
      multipleTimeScenario:
        marketingFlowTrigger?.max_triggers_per_user > 1 || marketingFlowTrigger?.max_triggers_per_user === null,
      ...formatCheckinCountValue(marketingFlowTrigger?.checkin_count),
      hasEndDate
    }

    let delaySettingsValue

    if (marketingFlowTrigger?.type === 'BIRTHDAY') {
      triggerSettingsValue.maxTriggersPerUser = 99

      delaySettingsValue = {
        daysBeforeAction: 0,
        sendImmediatlyAfterTrigger: false,
        sendAfterDelay: true,
        sendAtSpecificHour: true,
        delayUnit: 'DAYS',
        hourToSend: marketingFlow.action_delay_time,
        hourToSendLabel: marketingFlow.action_delay_time
      }
    } else {
      const hasSpecificHour = marketingFlow.action_delay_time !== null
      const isImmediateSend =
        (marketingFlow.action_delay_in_minutes === 0 || marketingFlow.action_delay_in_minutes === null) &&
        !hasSpecificHour

      delaySettingsValue = {
        daysBeforeAction: isImmediateSend
          ? null
          : getDelayMinutesDivValueFromUnit(marketingFlow.action_delay_in_minutes, marketingFlow.action_delay_unit),
        sendImmediatlyAfterTrigger: isImmediateSend,
        sendAfterDelay: !isImmediateSend,
        sendAtSpecificHour: hasSpecificHour,
        delayUnit: marketingFlow.action_delay_unit,
        hourToSend: marketingFlow.action_delay_time,
        hourToSendLabel: marketingFlow.action_delay_time
      }
    }

    const longestStoreName = marketingFlow.stores.reduce(
      (longest, current) => (current.name.length > longest.name.length ? current : longest),
      {
        name: ''
      }
    ).name

    const rewardAction = marketingFlow.actions.find((action) => action.type === 'REWARD')
    const bonusAction = marketingFlow.actions.find((action) => action.type === 'BONUS_POINTS')
    const messageAction = marketingFlow.actions.find(
      (action) => action.type === 'SEND_SMS' || action.type === 'SEND_PUSH'
    )

    const actionSettingsValue = {
      actions: marketingFlow.actions.map((action) => {
        const authentication = action.external_api?.authentication
        const legacyAuthConfig = getLegacyExternalApiAuthConfig(action.external_api?.auth_config)
        const externalApiAuthType = authentication?.type ?? action.external_api?.auth_type ?? 'NONE'
        const externalApiAuthConfig =
          externalApiAuthType === 'BEARER' && typeof legacyAuthConfig?.token === 'string'
            ? { token: legacyAuthConfig.token }
            : externalApiAuthType === 'BASIC' &&
                typeof legacyAuthConfig?.username === 'string' &&
                typeof legacyAuthConfig?.password === 'string'
              ? { username: legacyAuthConfig.username, password: legacyAuthConfig.password }
              : null

        return {
          actionType: action.type,
          messageContent: action.message_content ?? '',
          messageContentPreview: getMarketingFlowPreviewText(
            action.message_content,
            longestStoreName,
            rewardAction ? rewards?.find((reward) => reward.id === rewardAction.reward_id) : undefined,
            bonusAction ? bonusAction.bonus_points : undefined
          ),
          bonusMultiplier: action.bonus_multiplier,
          bonusPoints: isNumber(action.bonus_points) ? Number(action.bonus_points) : null,
          isTriggeredOnClick: Boolean(action.is_triggered_on_click),
          selectedReward: rewards?.find((reward) => reward.id === action.reward_id)?.id,
          selectedEmailTemplate: action.email_content_id ? String(action.email_content_id) : null,
          preferPushChannel: Boolean(action.type === 'SEND_SMS' && action.is_other_channel_active),
          preferSmsChannel: Boolean(action.type === 'SEND_PUSH' && action.is_other_channel_active),
          externalApiUrl: action.external_api?.url ?? null,
          externalApiMethod: action.external_api?.method ?? null,
          externalApiHeaders: action.external_api?.headers
            ? Object.entries(action.external_api.headers).map(([key, value]) => ({ key, value: String(value) }))
            : [{ key: '', value: '' }],
          externalApiBodyTemplate: action.external_api?.body_template ?? null,
          externalApiAuthType,
          externalApiAuthConfig,
          externalApiAuthPreview: authentication?.preview ?? null,
          isExternalApiAuthConfigDirty: false,
          isExternalApiAuthConfigSet: authentication?.config_set ?? action.external_api?.auth_config_set ?? false
        }
      }),

      isTransactional:
        messageAction?.is_transactional ?? isTransactionalDefaultTrigger(triggerSettingsValue.triggerType),
      conversionDelay: marketingFlow.conversion_delay_in_minutes / 24 / 60
    }

    return {
      triggerSettingsValue,
      delaySettingsValue,
      actionSettingsValue,
      lastUpdatedAtValue,
      marketingFlowNameValue,
      isActiveValue,
      activatedAtValue,
      templateUsedValue,
      scopeValue
    }
  }

  const fetchMarketingFlowPerformance = async ({ marketingFlowId }: { marketingFlowId: number }) => {
    const { data } = await axios.get(`/marketing-flows/${marketingFlowId}/customer-event-counts`)

    return {
      triggeredCustomers:
        Object.values(data as Record<string, number>).reduce((acc, curr) => {
          return acc + curr
        }, 0) ?? 0,
      completedCustomers: data.completed ?? 0,
      delayedCustomers: (data.in_progress ?? 0) + (data.not_started ?? 0)
    }
  }

  const saveMarketingFlow = async ({
    marketingFlowName,
    merchantId,
    isActive,
    triggerSettings,
    delaySettings,
    actionSettings,
    marketingFlowId,
    useDeactivate,
    executeOnExistingMembers,
    scope
  }: {
    marketingFlowName: string
    merchantId: number
    isActive: boolean
    triggerSettings: TriggerSettings
    delaySettings: DelaySettings
    actionSettings: ActionSettings
    marketingFlowId?: number
    useDeactivate?: boolean
    forceEdit?: boolean
    executeOnExistingMembers?: boolean
    scope?: ContentScope
  }) => {
    const payload = generateUpsertPayload({
      marketingFlowName,
      merchantId,
      isActive,
      actionSettings,
      delaySettings,
      triggerSettings,
      executeOnExistingMembers
    })

    let queryResult

    try {
      if (marketingFlowId) {
        const p = { payload, merchantId, marketingFlowId }
        let promise
        if (useDeactivate) {
          promise = deactivateMarketingFlow(p)
        } else {
          promise = editMarketingFlow({ ...p, scope })
        }
        const { data } = await promise
        queryResult = data
      } else {
        const { data } = await createMarketingFlow({ payload, scope })
        queryResult = data
      }

      return {
        lastUpdatedAtValue: getMarketingFlowLastUpdateDate(queryResult.data),
        marketingFlowIdValue: queryResult.data.id
      }
    } catch (error) {
      if (error?.response?.data.message === 'Cannot activate a marketing flow with processing segment or import') {
        throw new Error('processing import or segment')
      }
      throw error
    }
  }

  const createMarketingFlow = async ({
    payload,
    scope
  }: {
    payload: MarketingFlowCreatePayload
    scope?: ContentScope
  }) => {
    return await axios.post(`/marketing-flows`, {
      ...payload,
      template_used: templateUsed.value,
      ...(scope && { scope })
    })
  }

  const editMarketingFlow = async ({
    payload,
    marketingFlowId,
    scope
  }: {
    payload: MarketingFlowCreatePayload
    marketingFlowId: number
    scope?: ContentScope
  }) => {
    return await axios.put(`/marketing-flows/${marketingFlowId}`, { ...payload, ...(scope && { scope }) })
  }

  const deactivateMarketingFlow = async ({
    payload,
    marketingFlowId
  }: {
    payload: MarketingFlowCreatePayload
    marketingFlowId: number
  }) => {
    return await axios.put(`/marketing-flows/${marketingFlowId}/deactivate`, payload)
  }

  const deleteMarketingFlow = async ({ marketingFlowId }: { marketingFlowId: number }) => {
    return await axios.delete(`/marketing-flows/${marketingFlowId}`)
  }

  return {
    createMarketingFlow,
    deleteMarketingFlow,
    editMarketingFlow,
    fetchMarketingFlow,
    fetchMarketingFlowPerformance,
    generateNewVersionName,
    generateUpsertPayload,
    getMarketingFlowLastUpdateDate,
    isActionStepCompleted,
    isDelayStepCompleted,
    isTriggerStepCompleted,
    getMarketingFlowErrors,
    getActionStepErrors,
    getDelayStepErrors,
    getTriggerStepErrors,
    isUsingTemplate,
    saveMarketingFlow,
    templateUsed
  }
}
